var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useMemo, useState } from 'react';
import { ChatbotDataContext } from './ChatbotDataContext';
import { useLivePersonConnectionRequest } from '../LivePersonService/useLivePersonConnectionRequest';
import analytics from '../analytics';
var chatRguid = '';
/**
 * Data provider for Chatbot
 * @public
 */
export var ChatbotDataProvider = function (props) {
    var chatContainerHeight = props.chatContainerHeight, setCachedMessages = props.setCachedMessages, setCachedPrompts = props.setCachedPrompts, _a = props.cachedMessages, cachedMessages = _a === void 0 ? [] : _a, // Sending these will cause error in openAI but are good for display
    _b = props.cachedPrompts, // Sending these will cause error in openAI but are good for display
    cachedPrompts = _b === void 0 ? [] : _b, // Safe to send to openAI
    submitChatMessagesPayload = props.submitChatMessagesPayload, handleMarkerResponse = props.handleMarkerResponse, fetchRequestOptions = props.fetchRequestOptions, markerComponents = props.markerComponents, logFirstMessageSubmitted = props.logFirstMessageSubmitted, logCustomerCarePhoneNumberDisplayed = props.logCustomerCarePhoneNumberDisplayed, logCustomerCarePhoneNumberClicked = props.logCustomerCarePhoneNumberClicked, quickActions = props.quickActions, quickActionsStackVariant = props.quickActionsStackVariant, isMobile = props.isMobile, livePersonOptions = props.livePersonOptions, setiValues = props.setiValues, debugMode = props.debugMode, discoveryCitySelected = props.discoveryCitySelected, summaryUrl = props.summaryUrl, speechUrl = props.speechUrl, transcriptionUrl = props.transcriptionUrl;
    var _c = useState(''), errorMessage = _c[0], setErrorMessage = _c[1];
    var _d = useState(false), isLoading = _d[0], setLoadingStatus = _d[1];
    // ChatMessages keeps the data that would display to the user
    var _e = useState(__spreadArray([], cachedMessages, true)), chatMessages = _e[0], setChatMessage = _e[1];
    // Prompts keeps the data that would submit to the api
    var _f = useState(__spreadArray([], cachedPrompts, true)), prompts = _f[0], setPrompts = _f[1];
    var _g = useState(false), isDisablingInput = _g[0], setIsDisablingInputValue = _g[1];
    var _h = useState(''), fullStreamingTexts = _h[0], setStreamingText = _h[1];
    // Live Person state
    var _j = useState('AG'), livePersonAgentInitial = _j[0], setLivePersonAgentInitial = _j[1];
    var _k = useState('agent'), livePersonAgentName = _k[0], setLivePersonAgentName = _k[1];
    var _l = useState(''), lpUserFirstName = _l[0], setLPUserFirstName = _l[1];
    var _m = useState(''), lpUserLastName = _m[0], setLPUserLastName = _m[1];
    var _o = useState(''), livePersonConversationId = _o[0], setLivePersonConversationId = _o[1];
    var _p = useState(false), isLPUserFormSubmitted = _p[0], setIsLPUserFormSubmitted = _p[1];
    var _q = useState(false), isLivePersonEnabled = _q[0], setIsLivePersonEnabled = _q[1];
    var _r = useState(false), isLivePersonSessionActive = _r[0], setIsLivePersonSessionActive = _r[1];
    var _s = useState(false), closeLpChat = _s[0], setCloseLpChat = _s[1];
    var _t = useState(''), chatSummary = _t[0], setChatSummary = _t[1];
    var funcValues = useMemo(function () { return ({
        setErrorMessage: setErrorMessage,
        setLoadingStatus: setLoadingStatus,
        setChatMessage: setChatMessage,
        setPrompts: setPrompts,
        setIsDisablingInputValue: setIsDisablingInputValue,
        setStreamingText: setStreamingText,
        setLivePersonAgentInitial: setLivePersonAgentInitial,
        setLivePersonAgentName: setLivePersonAgentName,
        setLPUserFirstName: setLPUserFirstName,
        setLPUserLastName: setLPUserLastName,
        setIsLPUserFormSubmitted: setIsLPUserFormSubmitted,
        setIsLivePersonEnabled: setIsLivePersonEnabled,
        setChatSummary: setChatSummary,
        setIsLivePersonSessionActive: setIsLivePersonSessionActive,
        setCloseLpChat: setCloseLpChat,
    }); }, [
        setErrorMessage,
        setLoadingStatus,
        setChatMessage,
        setPrompts,
        setIsDisablingInputValue,
        setStreamingText,
        setLivePersonAgentInitial,
        setLivePersonAgentName,
        setLPUserFirstName,
        setLPUserLastName,
        setIsLPUserFormSubmitted,
        setIsLivePersonEnabled,
        setChatSummary,
        setIsLivePersonSessionActive,
        setCloseLpChat,
    ]);
    // Submit initiate message to start the conversation
    // this initial request require user first and last name
    var livePersonConnectionRequest = useLivePersonConnectionRequest({
        chatMessages: chatMessages,
        setChatMessage: setChatMessage,
        setCachedMessages: setCachedMessages,
        setIsDisablingInputValue: setIsDisablingInputValue,
        setStreamingText: setStreamingText,
        setErrorMessage: setErrorMessage,
        livePersonOptions: livePersonOptions,
        lpUserFirstName: lpUserFirstName,
        lpUserLastName: lpUserLastName,
        setLivePersonAgentInitial: setLivePersonAgentInitial,
        setLivePersonAgentName: setLivePersonAgentName,
        setLivePersonConversationId: setLivePersonConversationId,
        isLivePersonEnabled: isLivePersonEnabled,
        setIsLivePersonEnabled: setIsLivePersonEnabled,
        livePersonConversationId: livePersonConversationId,
        chatSummary: chatSummary,
        isLivePersonSessionActive: isLivePersonSessionActive,
        setIsLivePersonSessionActive: setIsLivePersonSessionActive,
        closeLpChat: closeLpChat,
        setCloseLpChat: setCloseLpChat,
        livePersonAgentName: livePersonAgentName,
    });
    // Preserve chatRguid from submitChatMessagesPayload
    // So on client side, once the Dialog is closed and reopen, there will be a new rguid
    // we need keep to use the same chatRguid.
    if (!chatRguid) {
        chatRguid = submitChatMessagesPayload.rguid;
    }
    else {
        submitChatMessagesPayload.rguid = chatRguid;
    }
    var contextValues = __assign({ discoveryCitySelected: discoveryCitySelected, chatContainerHeight: chatContainerHeight, setCachedMessages: setCachedMessages, setCachedPrompts: setCachedPrompts, cachedMessages: cachedMessages, cachedPrompts: cachedPrompts, submitChatMessagesPayload: submitChatMessagesPayload, handleMarkerResponse: handleMarkerResponse, fetchRequestOptions: fetchRequestOptions, markerComponents: markerComponents, logFirstMessageSubmitted: logFirstMessageSubmitted, logCustomerCarePhoneNumberDisplayed: logCustomerCarePhoneNumberDisplayed, logCustomerCarePhoneNumberClicked: logCustomerCarePhoneNumberClicked, quickActions: quickActions, quickActionsStackVariant: quickActionsStackVariant, isMobile: isMobile, livePersonOptions: livePersonOptions, debugMode: debugMode, 
        // global state values
        errorMessage: errorMessage, isLoading: isLoading, chatMessages: chatMessages, prompts: prompts, isDisablingInput: isDisablingInput, fullStreamingTexts: fullStreamingTexts, livePersonAgentInitial: livePersonAgentInitial, livePersonAgentName: livePersonAgentName, lpUserFirstName: lpUserFirstName, lpUserLastName: lpUserLastName, livePersonConnectionRequest: livePersonConnectionRequest, livePersonConversationId: livePersonConversationId, isLPUserFormSubmitted: isLPUserFormSubmitted, isLivePersonEnabled: isLivePersonEnabled, isLivePersonSessionActive: isLivePersonSessionActive, closeLpChat: closeLpChat, setiValues: setiValues, summaryUrl: summaryUrl, speechUrl: speechUrl, transcriptionUrl: transcriptionUrl }, funcValues);
    useEffect(function () {
        analytics.init(contextValues);
    }, []);
    return React.createElement(ChatbotDataContext.Provider, { value: contextValues }, props.children);
};
//# sourceMappingURL=ChatbotDataProvider.js.map